//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import * as Schema from 'effect/Schema';

import { Annotation, DXN, Obj, Ref, Type } from '@dxos/echo';
import { Format } from '@dxos/echo/Format';
import { PropertyMeta } from '@dxos/echo/internal';
import { CardAnnotation } from '@dxos/schema';

import * as Geo from './Geo.ts';
import * as Organization from './Organization.ts';

// TODO(burdon): Materialize link for Role (Organization => [Role] => Contact).
// TODO(burdon): Address sub type with geo location.
// TODO(burdon): Reconcile with user id.

/**
 * https://schema.org/Person
 * Based on fields from Apple Contacts.
 */
const PersonSchema = Schema.Struct({
  fullName: Schema.String.pipe(
    Schema.annotate({ title: 'Full Name' }),
    Annotation.GeneratorAnnotation.set({
      generator: 'person.fullName',
      probability: 1,
    }),
    Schema.optional,
  ),
  preferredName: Schema.String.pipe(Schema.annotate({ title: 'Preferred Name' }), Schema.optional),
  nickname: Schema.String.pipe(Schema.annotate({ title: 'Nickname' }), Schema.optional),
  // TODO(wittjosiah): Format.URL. Support ref?
  image: Schema.String.pipe(
    Schema.annotate({ title: 'Image' }),
    Annotation.GeneratorAnnotation.set('image.url'),
    Schema.optional,
  ),
  // TODO(burdon): Use reference links.
  organization: Ref.Ref(Organization.Organization).pipe(
    PropertyMeta('referenceProperty', 'name'),
    Schema.annotate({
      title: 'Employer',
      description: 'Current employer',
    }),
    Schema.optional,
  ),
  jobTitle: Schema.String.pipe(Schema.annotate({ title: 'Job Title' }), Schema.optional),
  department: Schema.String.pipe(Schema.annotate({ title: 'Department' }), Schema.optional),
  notes: Schema.String.pipe(Schema.annotate({ title: 'Notes' }), Schema.optional),
  emails: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Format.Email.pipe(Annotation.GeneratorAnnotation.set('internet.email')),
    }),
  ).pipe(Schema.optional),
  identities: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      // TODO(burdon): Identity types? (socials, DIDs, etc.)
      value: Schema.String,
    }),
  ).pipe(Schema.optional),
  phoneNumbers: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      // TODO(wittjosiah): Format.Phone.
      value: Schema.String,
    }),
  )
    .pipe(Schema.optional)
    .annotate({ title: 'Phone Numbers' }),
  addresses: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Geo.PostalAddress,
    }),
  ).pipe(Schema.optional),
  urls: Schema.Array(
    Schema.Struct({
      label: Schema.optional(Schema.String),
      value: Format.URL.pipe(
        Schema.annotate({ default: 'https://example.com' }),
        Annotation.GeneratorAnnotation.set('internet.url'),
      ),
    }),
  ).pipe(Schema.optional),
  // TODO(burdon): Support date or create String type for ISO Date.
  birthday: Schema.String.pipe(
    Schema.annotate({ title: 'Birthday' }),
    Annotation.GeneratorAnnotation.set('date.iso8601'),
    Schema.optional,
  ),
  // TODO(burdon): Move to base object?
  fields: Schema.Array(
    Schema.Struct({
      category: Schema.optional(Schema.String),
      label: Schema.String,
      value: Schema.String,
    }),
  ).pipe(Schema.optional),
});

const _PersonSchema = PersonSchema.pipe(
  Schema.fieldsAssign({
    // TODO(wittjosiah): Reconcile with addresses.
    location: Format.GeoPoint.pipe(Schema.annotate({ title: 'Location' }), Schema.optional),
  }),
  Schema.annotate({ title: 'Person' }),
  Annotation.LabelAnnotation.set(['preferredName', 'fullName', 'nickname']),
  Annotation.IconAnnotation.set({ icon: 'ph--user--regular', hue: 'neutral' }),
  CardAnnotation.set(true),
);

export class Person extends Type.makeObject<Person>(DXN.make('org.dxos.type.person', '0.1.0'))(
  _PersonSchema.pipe(Annotation.UserType.set()),
) {}

export const make = (props: Partial<Obj.MakeProps<typeof Person>> = {}) => Obj.make(Person, props);

const _LegacyPersonSchema = PersonSchema.pipe(
  Schema.annotate({ title: 'Person' }),
  Annotation.LabelAnnotation.set(['preferredName', 'fullName', 'nickname']),
);

/**
 * @deprecated
 */
export const LegacyPerson = Type.makeObject(DXN.make('org.dxos.type.person', '0.1.0'))(_LegacyPersonSchema);
